import React, { useEffect, useState } from 'react';
import api, { unwrap } from '../utils/api';

const POLL_MS = 60_000;
// Last answer, so a visitor who already saw the lock screen doesn't get a
// flash of the site before it.
const CACHE_KEY = 'itsasimcha_maintenance_on';
const cachedOn = () => { try { return localStorage.getItem(CACHE_KEY) === '1'; } catch (_) { return false; } };

// Wraps the entire customer app. Asks the backend whether the public site is
// in maintenance mode, and if so renders only a logo + message — no header,
// no routes. Re-polls so flipping the toggle in admin propagates without
// the visitor having to refresh.
export default function MaintenanceGate({ children }) {
  // The site renders right away; the check runs in the background and swaps
  // in the lock screen only if maintenance is on (waiting for it used to
  // leave every first page load blank for a moment).
  const [state, setState] = useState(() => ({ loaded: !cachedOn(), enabled: false, message: '' }));

  // Allow the admin to preview their own site while locked: ?preview=1 on
  // any URL bypasses the gate for that browser session.
  const sessionPreview = (() => {
    try {
      if (new URLSearchParams(window.location.search).get('preview') === '1') {
        sessionStorage.setItem('itsasimcha_preview', '1');
      }
      return sessionStorage.getItem('itsasimcha_preview') === '1';
    } catch (_) { return false; }
  })();

  useEffect(() => {
    let cancelled = false;
    async function check() {
      try {
        const r = await api.get('/miscellaneous/maintenance');
        const v = unwrap(r) || { enabled: false };
        if (cancelled) return;
        try { localStorage.setItem(CACHE_KEY, v.enabled ? '1' : '0'); } catch (_) { /* private mode */ }
        setState({ loaded: true, enabled: !!v.enabled, message: v.message || '' });
      } catch (_) {
        // If the API is unreachable, fail open (render the site).
        if (cancelled) return;
        setState({ loaded: true, enabled: false, message: '' });
      }
    }
    check();
    const t = setInterval(check, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Only when this browser last saw maintenance on: wait for the first check
  // instead of flashing the site.
  if (!state.loaded) return null;

  if (state.enabled && !sessionPreview) {
    return (
      <div style={{
        position: 'fixed', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', padding: 24,
        background: '#f4f9fd',
        color: '#1a2533',
        fontFamily: 'inherit',
      }}>
        <img
          src="/logo.png"
          alt="It's a Simcha"
          style={{ maxWidth: 280, width: '70%', height: 'auto', marginBottom: 32 }}
          onError={e => { e.currentTarget.style.display = 'none'; }}
        />
        <h1 style={{ fontSize: 32, margin: '0 0 12px', color: '#29B4F1' }}>We'll be back soon</h1>
        <p style={{ fontSize: 17, color: '#5a6a7a', maxWidth: 520, margin: '0 0 24px', lineHeight: 1.6 }}>
          {state.message || 'Our site is briefly down for maintenance. Please check back in a little while — thank you for your patience!'}
        </p>
        <p style={{ fontSize: 13, color: '#9aa5b0' }}>
          — It's a Simcha
        </p>
      </div>
    );
  }

  return children;
}
